import React, { useLayoutEffect, useMemo, useState } from 'react'
import { Input, Modal, Spin, Table } from '@surecompanies/core_components'
import { useQuery } from '@tanstack/react-query'
import { getCensusSnapshotPreview } from './census-run-api'

/**
 * One stage's snapshot, read on screen.
 *
 * These rows are census data, so the modal shows them and nothing else — no
 * copy of the file is kept in the page once it closes.
 */
function SnapshotPreviewModal({
  runId,
  stepKey,
  stageName,
  file,
  onClose,
}: {
  runId: string
  stepKey: string
  stageName: string
  file: string
  onClose: () => void
}) {
  const [search, setSearch] = useState('')

  // Opening the modal locks the document scroll, which drops the reader back to
  // the top of a long run. The position is taken before the lock applies and put
  // back on the same frame, and again when the modal closes and the lock lifts,
  // so the row they opened it from is still where they left it.
  useLayoutEffect(() => {
    const top = window.scrollY
    const restore = () => window.scrollTo({ top })
    const frame = requestAnimationFrame(restore)
    return () => {
      cancelAnimationFrame(frame)
      requestAnimationFrame(restore)
    }
  }, [])

  const { data, isPending, isError } = useQuery({
    queryKey: ['census-snapshot-preview', runId, stepKey, file],
    queryFn: () => getCensusSnapshotPreview(runId, stepKey, file),
    staleTime: 60 * 1000,
  })

  const rows = useMemo(() => {
    const all = data?.rows ?? []
    const term = search.trim().toLowerCase()
    if (term === '') return all
    return all.filter((row) =>
      Object.values(row).some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(term)
      )
    )
  }, [data, search])

  // Census headers are long words like DateOfBirth, and a column narrow enough
  // to cut the header makes the table unreadable. Fixed width plus horizontal
  // scroll beats squeezing every column into the modal.
  const columns = (data?.columns ?? []).map((column) => ({
    title: column,
    dataIndex: column,
    key: column,
    width: Math.max(140, column.length * 9 + 32),
    ellipsis: true,
    render: (value: string) => value || '—',
  }))

  return (
    <Modal
      open
      onCancel={onClose}
      onOk={onClose}
      width={980}
      centered
      okText="Close"
      cancelButtonProps={{ style: { display: 'none' } }}
      title={
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#9ca3af',
            }}
          >
            Stage snapshot
          </div>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{stageName}</div>
          <div style={{ fontFamily: 'monospace', color: '#6b7280' }}>
            {file}
          </div>
        </div>
      }
    >
      {isPending && (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
          <Spin />
        </div>
      )}
      {isError && (
        <div style={{ padding: '16px 0', color: '#7a1f22' }}>
          Could not read this snapshot.
        </div>
      )}
      {data?.text !== undefined && (
        <pre
          style={{
            margin: 0,
            maxHeight: 480,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: 12.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {data.text}
        </pre>
      )}
      {data && data.text === undefined && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#9ca3af',
              }}
            >
              Record preview
            </div>
            <Input
              placeholder="Search any field"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 260 }}
              allowClear
            />
            <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>
              {data.totalRows} record{data.totalRows === 1 ? '' : 's'}
            </span>
          </div>
          {/* Stated on every preview, not only a truncated one, so nobody has
              to wonder whether they are looking at the whole file. */}
          <div
            style={{
              marginBottom: 12,
              padding: '8px 12px',
              // Red, not amber: a reader who misses this is looking at part of
              // a file and has no way to tell from the rows themselves.
              background: data.isTruncated ? '#fef2f2' : '#f9fafb',
              border: `1px solid ${data.isTruncated ? '#fecaca' : '#e5e7eb'}`,
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: data.isTruncated ? 500 : undefined,
              color: data.isTruncated ? '#7a1f22' : '#6b7280',
            }}
          >
            {data.isTruncated
              ? `A preview shows at most ${data.rowLimit.toLocaleString()} rows. This file has ${data.totalRows.toLocaleString()} — download it to read the rest.`
              : `All ${data.totalRows.toLocaleString()} row(s) shown. A preview shows at most ${data.rowLimit.toLocaleString()}; a longer file has to be downloaded to be read in full.`}
          </div>
          <Table
            dataSource={rows}
            columns={columns as any}
            rowKey={(_row: Record<string, string>, index?: number) =>
              String(index)
            }
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: false }}
            scroll={{ x: 'max-content', y: 420 }}
            locale={{ emptyText: 'No rows in this snapshot' }}
          />
        </>
      )}
    </Modal>
  )
}

export default SnapshotPreviewModal
